import React, { useState, useEffect, useCallback } from 'react';
import { Button, Row, Col, Form, Modal, Container, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  CreditCard,
  Banknote,
  Mail
} from 'lucide-react';
import { Clienti, PrestazioniProfile, FatturaEditFormData, Storico, Fattura, Profile } from '../types';
import apiService from '../services/api';
import { toast } from 'react-toastify';
import FatturaPDF from '../components/FatturaPDF';
import EmailComposeModal from '../components/EmailComposeModal';

const Fatture: React.FC = () => {
  const navigate = useNavigate();
  const { specialtyId } = useParams<{ specialtyId: string }>();
  const [fatture, setFatture] = useState<Storico[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [, setClienti] = useState<Clienti[]>([]);
  const [, setPrestazioni] = useState<PrestazioniProfile[]>([]);
  const [editingFattura, setEditingFattura] = useState<Storico | null>(null);

  // Stati per l'anteprima PDF
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedFattura, setSelectedFattura] = useState<Storico | null>(null);
  const [selectedFatturaCompleta, setSelectedFatturaCompleta] = useState<Fattura | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [emailStorico, setEmailStorico] = useState<Storico | null>(null);

  // Stati per la paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(7);
  const [formData, setFormData] = useState<FatturaEditFormData>({
    numero_fattura: '',
    data: '',
    anno: new Date().getFullYear(),
    onorario: '',
    contributo_enpav: '',
    totale: '',
    bollo: '',
    tipo_pagamento: 'POS',
    cliente_id: 0,
    prestazioni: [],
  });

  const loadData = useCallback(async (page: number = currentPage) => {
    try {
      setLoading(true);
      
      // Determina il valore della specializzazione da passare alle API
      let specialisticaValue: number | undefined;
      if (specialtyId) {
        // Usa direttamente l'ID dalla URL
        specialisticaValue = parseInt(specialtyId);
      }

      const [fattureRes, clientiRes, prestazioniRes] = await Promise.all([
        apiService.getFatture(specialisticaValue, page, pageSize),
        apiService.getClienti(),
        apiService.getPrestazioni(),
      ]);
      
      // Aggiungo controlli di sicurezza per evitare errori se results è undefined
      setFatture(fattureRes?.results || []);
      setClienti(clientiRes.results.map(item => item.cliente));
      setPrestazioni(prestazioniRes.results);
      
      // Aggiorna i dati di paginazione
      if (fattureRes) {
        setTotalPages(fattureRes.num_pages || 1);
        setTotalCount(fattureRes.count || 0);
        setCurrentPage(fattureRes.current_page || page);
      }
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
      toast.error('Errore nel caricamento dei dati');
      // In caso di errore, impostiamo array vuoti per evitare errori di undefined
      setFatture([]);
      setTotalPages(1);
      setTotalCount(0);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }, [specialtyId, currentPage, pageSize]);

  useEffect(() => {
    if (specialtyId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyId]); // Rimossa dipendenza loadData per evitare chiamate duplicate

  // Funzioni per la paginazione
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadData(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingFattura) {
        await apiService.updateFattura(editingFattura.fattura.id, formData);
        toast.success('Fattura aggiornata con successo');
        setShowModal(false);
        resetForm();
        // Reset alla prima pagina dopo il salvataggio
        setCurrentPage(1);
        loadData(1);
      } else {
        // Reindirizza alla pagina di creazione fattura
        navigate(`/fatture/nuova`);
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleEdit = (storico: Storico) => {
    setEditingFattura(storico);
    setFormData({
      numero_fattura: storico.fattura.numero,
      data: storico.fattura.data,
      anno: storico.fattura.anno,
      onorario: storico.fattura.onorario,
      contributo_enpav: storico.fattura.contributo_enpav,
      totale: storico.fattura.totale,
      bollo: storico.fattura.bollo || '',
      tipo_pagamento: storico.fattura.tipo_pagamento,
      cliente_id: storico.clienti.id,
      prestazioni: [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questa fattura?')) {
      try {
        await apiService.deleteFattura(id);
        toast.success('Fattura eliminata con successo');
        // Reset alla prima pagina dopo l'eliminazione
        setCurrentPage(1);
        loadData(1);
      } catch (error) {
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  const handlePreview = async (storico: Storico) => {
    setSelectedFattura(storico);
    setLoadingPDF(true);
    
    try {
      // Carica i dati completi della fattura
      console.log('🔍 Caricamento dati completi fattura ID:', storico.fattura.id);
      const fatturaResponse = await apiService.getFattura(storico.fattura.id);
      console.log('✅ Dati completi ricevuti:', fatturaResponse);
      
      // Adatta i dati alla struttura del componente FatturaPDF
      const fatturaCompleta: Fattura = fatturaResponse.fattura;
      setSelectedFatturaCompleta(fatturaCompleta);
      
      // Aggiorna i dati del cliente e della fattura selezionata
      setSelectedFattura(fatturaResponse);
      
      // Usa il profilo dalla risposta API
      setProfile(fatturaResponse.profile);
      
    } catch (error) {
      console.error('❌ Errore nel caricamento dei dati completi:', error);
      toast.error('Errore nel caricamento dei dettagli della fattura');
      // Fallback ai dati base
      setSelectedFatturaCompleta(storico.fattura);
    } finally {
      setLoadingPDF(false);
    }
    setShowPDFModal(true);
  };

  const handleDownload = async (storico: Storico) => {
    try {
      const blob = await apiService.downloadFatturaPDF(storico.fattura.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fattura-${storico.fattura.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const resetForm = () => {
    setFormData({
      numero_fattura: '',
      data: '',
      anno: new Date().getFullYear(),
      onorario: '',
      contributo_enpav: '',
      totale: '',
      bollo: '',
      tipo_pagamento: 'POS',
      cliente_id: 0,
      prestazioni: [],
    });
    setEditingFattura(null);
  };

  if (loading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div className="text-center p-5">
            <div className="modern-spinner mx-auto mb-3"></div>
            <p className="text-muted">Caricamento fatture...</p>
          </div>
        </Container>
      </div>
    );
  }

  // Helper di sola presentazione per la lista
  const formatData = (value: string) => {
    const d = new Date(value);
    return isNaN(d.getTime())
      ? value
      : d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatEuro = (value: string | number) => {
    const n = Number(value);
    return isNaN(n) ? String(value) : n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const iniziali = (storico: Storico) =>
    `${storico.clienti.nome?.[0] || ''}${storico.clienti.cognome?.[0] || ''}`.toUpperCase();

  const totalePagina = fatture.reduce((somma, storico) => somma + (Number(storico.fattura.totale) || 0), 0);
  const nPos = fatture.filter((storico) => storico.fattura.tipo_pagamento === 'POS').length;
  const nContanti = fatture.length - nPos;

  return (
    <div className="page-container">
      <Container fluid>
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>Gestione Fatture</h1>
              <p className="text-muted">Visualizza e gestisci tutte le fatture</p>
            </div>
            <Button 
              variant="primary" 
              onClick={() => navigate('/fatture/nuova')}
              className="btn-modern d-flex align-items-center gap-2"
            >
              <Plus size={20} />
              Nuova Fattura
            </Button>
          </div>
        </div>

        <section className="inv-stats">
          <div className="inv-stat">
            <span>Fatture totali</span>
            <strong>{totalCount}</strong>
          </div>
          <div className="inv-stat">
            <span>Totale di questa pagina</span>
            <strong>€ {formatEuro(totalePagina)}</strong>
          </div>
          <div className="inv-stat">
            <span>Pagamenti in questa pagina</span>
            <strong className="inv-stat-pay">
              <span><CreditCard size={16} /> {nPos} POS</span>
              <span><Banknote size={16} /> {nContanti} contanti</span>
            </strong>
          </div>
        </section>

        <div className="inv-list">
          <div className="inv-head">
            <span>Fattura</span>
            <span>Cliente</span>
            <span>Pagamento</span>
            <span className="text-end">Totale</span>
            <span />
          </div>

          {fatture.length === 0 && (
            <div className="set-empty m-3">
              <FileText size={34} />
              <strong>Nessuna fattura</strong>
              <span>Le fatture che crei compariranno qui.</span>
            </div>
          )}

          {fatture.map((storico, index) => (
            <article key={storico.id} className="inv-row" style={{ animationDelay: `${index * 0.04}s` }}>
              <div className="inv-num">
                <strong>#{storico.fattura.numero}</strong>
                <small>{formatData(storico.fattura.data)}</small>
              </div>

              <div className="inv-client">
                <span className="inv-avatar">{iniziali(storico)}</span>
                <div>
                  <strong>{storico.clienti.nome} {storico.clienti.cognome}</strong>
                  <small>{storico.clienti.citta}</small>
                </div>
              </div>

              <div>
                <span className={`inv-pay ${storico.fattura.tipo_pagamento === 'POS' ? 'is-pos' : 'is-cash'}`}>
                  {storico.fattura.tipo_pagamento === 'POS' ? <CreditCard size={14} /> : <Banknote size={14} />}
                  {storico.fattura.tipo_pagamento}
                </span>
              </div>

              <div className="inv-total">
                <small>€</small>{formatEuro(storico.fattura.totale)}
              </div>

              <div className="inv-actions">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePreview(storico)}
                  className="btn-sm-modern"
                  title="Anteprima"
                >
                  <Eye size={14} />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setEmailStorico(storico)}
                  className="btn-sm-modern"
                  title="Invia per email"
                >
                  <Mail size={14} />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(storico.fattura.id)}
                  className="btn-sm-modern"
                  title="Elimina"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </article>
          ))}

          {/* Componente di paginazione */}
          {totalPages > 1 && (
            <div className="inv-foot">
              <div className="text-muted small">
                Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} di {totalCount} fatture
              </div>
              <div className="d-flex align-items-center gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="btn-sm-modern"
                >
                  <ChevronLeft size={14} />
                </Button>

                <div className="d-flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                    // Mostra solo alcune pagine intorno alla pagina corrente
                    const showPage = pageNum === 1 ||
                                   pageNum === totalPages ||
                                   (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                    if (!showPage) {
                      // Mostra "..." se ci sono pagine nascoste
                      if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return <span key={pageNum} className="px-1 small">...</span>;
                      }
                      return null;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="btn-sm-modern px-2"
                        style={{ minWidth: '32px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="btn-sm-modern"
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Modal per creazione/modifica */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" className="modern-modal">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Edit size={20} />
              {editingFattura ? 'Modifica Fattura' : 'Nuova Fattura'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} className="modern-form">
            <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Numero Fattura</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.numero_fattura}
                    onChange={(e) => setFormData({ ...formData, numero_fattura: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Anno</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.anno}
                    onChange={(e) => setFormData({ ...formData, anno: parseInt(e.target.value) })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo Pagamento</Form.Label>
                  <Form.Select
                    value={formData.tipo_pagamento}
                    onChange={(e) => setFormData({ ...formData, tipo_pagamento: e.target.value as 'POS' | 'CONTANTI' })}
                  >
                    <option value="POS">POS</option>
                    <option value="CONTANTI">Contanti</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Onorario</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.onorario}
                    onChange={(e) => setFormData({ ...formData, onorario: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contributo ENPAV</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.contributo_enpav}
                    onChange={(e) => setFormData({ ...formData, contributo_enpav: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Totale</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.totale}
                    onChange={(e) => setFormData({ ...formData, totale: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Bollo</Form.Label>
              <Form.Control
                type="text"
                value={formData.bollo}
                onChange={(e) => setFormData({ ...formData, bollo: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={() => setShowModal(false)}
                className="btn-modern"
              >
                Annulla
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                className="btn-modern d-flex align-items-center gap-2"
              >
                <Edit size={16} />
                {editingFattura ? 'Aggiorna' : 'Crea'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal per visualizzazione PDF */}
        <Modal 
          show={showPDFModal} 
          onHide={() => {
            setShowPDFModal(false);
            setSelectedFattura(null);
            setSelectedFatturaCompleta(null);
            setLoadingPDF(false);
          }} 
          size="xl"
          className="modern-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Eye size={20} />
              Anteprima Fattura PDF
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingPDF && (
              <div className="text-center p-5">
                <div className="modern-spinner mx-auto mb-3"></div>
                <p className="text-muted">Caricamento dati completi della fattura...</p>
              </div>
            )}
            {selectedFatturaCompleta && selectedFattura && profile && !loadingPDF && (
              <FatturaPDF 
                fattura={selectedFatturaCompleta} 
                cliente={selectedFattura.clienti}
                profile={profile}
                storicoId={selectedFattura.id}
              />
            )}
            {!loadingPDF && (!selectedFatturaCompleta || !selectedFattura || !profile) && (
              <div className="text-center p-5">
                <Alert variant="danger" className="modern-alert">
                  <p className="mb-0">Errore nel caricamento dei dati della fattura</p>
                </Alert>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {emailStorico && (
          <EmailComposeModal
            show
            onHide={() => setEmailStorico(null)}
            tipo="fattura"
            storicoId={emailStorico.id}
            etichetta={`Fattura n. ${emailStorico.fattura.numero}/${emailStorico.fattura.anno}`}
            cliente={emailStorico.clienti}
          />
        )}
      </Container>
    </div>
  );
};

export default Fatture; 